<script setup lang="ts">
import { computed, ref, watch, toRef } from 'vue';
import { storeToRefs } from 'pinia';

import TopPageNavigation from '@/components/TopPageNavigation.vue';
import ConsultationNavigationTabs from '@/components/ConsultationNavigationTabs.vue';
import FormCreateTab from '@/components/FormTabs/FormCreateTab.vue';
import FormEngageTab from '@/components/FormTabs/FormEngageTab.vue';
import FormAnalyzeTab from '@/components/FormTabs/FormAnalyzeTab.vue';

import type { ConsultationDto, FormDto, Locales, SectionDto } from '@/services/types/api.types';
import { useCommentsStore, useConsultationsStore, useFormsStore, useSectionsStore, useUsersStore } from '@/stores';
import { Constants } from '@/constants';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { getLanguageEnabled, getTranslation } from '@/composables/data-display-utils';
import { useRouteNumberParam } from '@/composables/use-route-param';
import { useFormBreadcrumbOptions, useSectionBreadcrumbOptions } from '@/composables/breadcrumb-options';

// PROPS
const props = withDefaults(
  defineProps<{
    isTemplate?: boolean;
  }>(),
  {
    isTemplate: false,
  },
);

// STORE
const formsStore = useFormsStore();
const sectionsStore = useSectionsStore();
const consultationsStore = useConsultationsStore();
const commentsStore = useCommentsStore();
const userStore = useUsersStore();
const { currentUser } = storeToRefs(userStore);

const formId = useRouteNumberParam('id');

// REF
const languageEnabled = ref<Locales[]>([]);
const activeTab = ref<string>('create');
const section = ref<SectionDto | undefined>(undefined);
const consultation = ref<ConsultationDto | undefined>(undefined);
const createTabRef = ref<InstanceType<typeof FormCreateTab> | null>(null);
const navigationTabsRef = ref<InstanceType<typeof ConsultationNavigationTabs> | null>(null);

const { sectionOptions } = useSectionBreadcrumbOptions(
  computed(() => consultation.value?.id),
  toRef(props, 'isTemplate'),
);

const { formOptions } = useFormBreadcrumbOptions(
  computed(() => section.value?.consultation_id),
  computed(() => section.value?.id),
  toRef(props, 'isTemplate'),
);

// FORM
const form = useWatchItemLoading<FormDto>(formId, formsStore);

// LIFECYCLE
watch(
  formId,
  async (newFormId) => {
    languageEnabled.value = [];
    section.value = undefined;
    consultation.value = undefined;
    if (newFormId < 0) {
      return;
    }
    if (activeTab.value !== 'create') {
      activeTab.value = 'create';
      navigationTabsRef.value?.setActiveTab('create');
    }

    await formsStore.fetchForm(newFormId);

    const loadedForm = formsStore.getItem(newFormId);
    if (!loadedForm) {
      return;
    }

    await commentsStore.fetchCommentsByFormId(newFormId);
    if (loadedForm.type === Constants.Form.Type.OpenQuestion || loadedForm.answers?.steps?.[0]?.otherChoice) {
      await formsStore.fetchVotes(newFormId);
    }

    if (!loadedForm.section_id) {
      languageEnabled.value = [];
      return;
    }

    await sectionsStore.fetchSection(loadedForm.section_id);
    const loadedSection = sectionsStore.getItem(loadedForm.section_id);
    section.value = loadedSection ?? undefined;
    if (loadedSection?.consultation_id) {
      await consultationsStore.fetchConsultation(loadedSection.consultation_id);
      const loadedConsultation = consultationsStore.getItem(loadedSection.consultation_id);
      consultation.value = loadedConsultation ?? undefined;
      languageEnabled.value = consultation.value ? getLanguageEnabled(consultation.value) : [];
    } else {
      consultation.value = undefined;
      languageEnabled.value = [];
    }
  },
  { immediate: true },
);

const breadcrumbs = computed(() => {
  return [
    {
      label: consultation.value?.title,
      to: {
        name: props.isTemplate ? 'consultation_template_details' : 'consultation_details',
        params: { id: consultation.value?.id },
      },
      icon: props.isTemplate ? 'o_settings' : 'o_folder_copy',
    },
    {
      label: section.value?.title,
      to: {
        name: props.isTemplate ? 'section_template_details' : 'section_details',
        params: { id: section.value?.id },
      },
      icon: 'o_folder',
      options: sectionOptions.value,
      activeId: section.value?.id,
    },
    {
      label: form.value?.question?.title,
      to: {
        name: props.isTemplate ? 'form_template_details' : 'form_details',
        params: { id: formId.value },
      },
      icon: 'o_article',
      options: formOptions.value,
      activeId: form.value?.id,
    },
  ];
});

const disableAnalyzeTab = computed(() => {
  return (
    !form.value?.are_comments_enabled &&
    form.value?.type !== Constants.Form.Type.OpenQuestion &&
    !form.value?.answers?.steps?.[0].otherChoice
  );
});

const preview = () => {
  const previewUrl = formsStore.previewURL(formId.value);
  if (previewUrl) {
    window.open(previewUrl, '_blank')?.focus();
  }
};

const handleTabChange = (tabId: string) => {
  if (activeTab.value === 'create') {
    createTabRef.value?.executeNavigationGuard({
      onOk: () => (activeTab.value = tabId),
      onCancel: () => navigationTabsRef.value?.setActiveTab('create'),
    });
  } else {
    activeTab.value = tabId;
  }
};
</script>
<template>
  <q-page class="form-detail">
    <top-page-navigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      :to="
        form
          ? {
              name: props.isTemplate ? 'section_template_details' : 'section_details',
              params: { id: form?.section_id },
            }
          : { name: 'home' }
      "
      @preview="preview"
    >
      <template #top-navigation>
        <consultation-navigation-tabs
          ref="navigationTabsRef"
          v-if="activeTab"
          :active-tab="activeTab"
          :is-template="isTemplate"
          :disabled-tabs="disableAnalyzeTab ? ['analyze'] : []"
          @update="handleTabChange"
        />
      </template>
    </top-page-navigation>
    <div class="title">
      <div>
        <q-icon name="o_article" />
        <h1>{{ getTranslation(form?.question, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div v-if="form">
      <div v-if="activeTab === 'create'">
        <form-create-tab
          :form-id="formId"
          :comments-enabled="form?.are_comments_enabled"
          :is-super-admin="currentUser?.isSuperadmin === true"
          :language-enabled="languageEnabled"
          :section="section"
          :consultation="consultation"
          ref="createTabRef"
        />
      </div>
      <div v-if="activeTab === 'engage'">
        <form-engage-tab />
      </div>
      <div v-if="activeTab === 'analyze'">
        <form-analyze-tab :form-id="formId" :comments-enabled="form?.are_comments_enabled" />
      </div>
    </div>
  </q-page>
</template>
<style lang="scss">
.form-detail {
  padding: 16px;

  .title {
    margin: 20px 0;
    padding: 0 10px;

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }
}
</style>
